import { onPageIn, onMount, onDestroy } from '@/modules/_'
import { Scroll } from '@/lib/scroll'
import { gsap } from 'gsap'

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const parseCustomStyles = (styleString: string): Record<string, string> => {
    const styles: Record<string, string> = {}
    const declarations = styleString.split(';').filter((decl) => decl.trim())

    declarations.forEach((declaration) => {
      const [property, value] = declaration.split(':').map((str) => str.trim())
      if (property && value) {
        styles[property] = value
      }
    })

    return styles
  }

  const applyCustomStyles = (): void => {
    // Only run on screens 767px and above
    if (window.innerWidth < 767) return

    const isMobile = window.innerWidth < 768
    const parentElement = element.parentElement as HTMLElement | null

    const allCards = document.querySelectorAll<HTMLElement>(
      '[data-visionaries="card"]'
    )
    const index = Array.from(allCards).indexOf(element)

    if (isMobile) {
      element.removeAttribute('style')

      element.style.aspectRatio = '241/181'

      const mediaWrap = element.querySelector<HTMLElement>(
        '.visionary_card_media_wrap'
      )
      if (mediaWrap) {
        mediaWrap.removeAttribute('style')
      }

      if (parentElement) {
        parentElement.style.removeProperty('margin-top')
        parentElement.classList.remove('is--right')

        if (index % 2 === 1) {
          parentElement.style.alignSelf = 'flex-end'
        } else {
          parentElement.style.alignSelf = 'flex-start'
        }
      }
    } else {
      const customStyleAttr = element.getAttribute('custom-style')

      if (customStyleAttr) {
        const styles = parseCustomStyles(customStyleAttr)

        if (styles.width) {
          element.style.width = styles.width
        }

        if (styles['aspect-ratio']) {
          const mediaWrap = element.querySelector<HTMLElement>(
            '.visionary_card_media_wrap'
          )
          if (mediaWrap) {
            mediaWrap.style.aspectRatio = styles['aspect-ratio']
          }
        }
      }

      if (parentElement) {
        if (index % 2 === 0) {
          parentElement.classList.add('is--right')
        } else {
          parentElement.classList.remove('is--right')
        }

        if (index % 2 === 1) {
          parentElement.style.marginTop = '230px'
        }
      }
    }
  }

  const mediaWrap = element.querySelector(
    '.visionary_card_media_wrap'
  ) as HTMLElement
  let tlIn: gsap.core.Timeline | null = null
  let tlOut: gsap.core.Timeline | null = null
  let handleMouseEnter: (() => void) | null = null
  let handleMouseLeave: (() => void) | null = null

  const setupHoverAnimation = (): void => {
    if (!mediaWrap) return

    const defaultContent = mediaWrap.querySelector(
      '.visionary_card_content.is--default'
    ) as HTMLElement
    const hoveredContent = mediaWrap.querySelector(
      '.visionary_card_content.is--hovered'
    ) as HTMLElement

    if (!defaultContent || !hoveredContent) return

    const easeOutQuart = 'cubic-bezier(0.165, 0.84, 0.44, 1)'
    const easeInOutCubic = 'cubic-bezier(0.65, 0, 0.35, 1)'
    const easeInOutQuad = 'cubic-bezier(0.45, 0, 0.55, 1)'

    const createHoverInTimeline = () => {
      return gsap
        .timeline({ paused: true })
        .to(
          defaultContent,
          {
            opacity: 0,
            y: -8,
            scale: 0.98,
            filter: 'blur(2px)',
            duration: 0.5,
            ease: easeInOutCubic,
            onComplete: () => {
              defaultContent.style.pointerEvents = 'none'
              defaultContent.style.userSelect = 'none'
            },
          },
          0
        )
        .fromTo(
          hoveredContent,
          {
            opacity: 0,
            y: 10,
            scale: 0.98,
            filter: 'blur(3px)',
          },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: 'blur(0px)',
            duration: 0.65,
            ease: easeOutQuart,
            onStart: () => {
              hoveredContent.style.pointerEvents = 'auto'
              hoveredContent.style.userSelect = 'auto'
            },
          },
          0.15
        )
    }

    const createHoverOutTimeline = () => {
      return gsap
        .timeline({ paused: true })
        .to(
          hoveredContent,
          {
            opacity: 0,
            y: -5,
            scale: 0.98,
            filter: 'blur(2px)',
            duration: 0.45,
            ease: easeInOutCubic,
            onComplete: () => {
              hoveredContent.style.pointerEvents = 'none'
              hoveredContent.style.userSelect = 'none'
            },
          },
          0
        )
        .to(
          defaultContent,
          {
            opacity: 1,
            y: 0,
            scale: 1,
            filter: 'blur(0px)',
            duration: 0.6,
            ease: easeOutQuart,
            onStart: () => {
              defaultContent.style.pointerEvents = 'auto'
              defaultContent.style.userSelect = 'auto'
            },
          },
          0.15
        )
    }

    handleMouseEnter = () => {
      if (window.innerWidth < 1024) return

      if (tlOut) tlOut.kill()
      if (!tlIn) tlIn = createHoverInTimeline()
      tlIn.restart()
    }

    handleMouseLeave = () => {
      if (window.innerWidth < 1024) return

      if (tlIn) tlIn.kill()
      if (!tlOut) tlOut = createHoverOutTimeline()
      tlOut.restart()
    }

    gsap.set(hoveredContent, {
      opacity: 0,
      pointerEvents: 'none',
      userSelect: 'none',
    })

    gsap.set(defaultContent, {
      opacity: 1,
      pointerEvents: 'auto',
      userSelect: 'auto',
    })

    // Create GSAP matchMedia instance
    const mm = gsap.matchMedia()

    // Only enable hover on devices with hover capability and fine pointer (mouse/trackpad)
    mm.add('(hover: hover) and (pointer: fine)', () => {
      if (handleMouseEnter && handleMouseLeave) {
        mediaWrap.addEventListener('mouseenter', handleMouseEnter)
        mediaWrap.addEventListener('mouseleave', handleMouseLeave)
      }

      // Cleanup function when media query stops matching
      return () => {
        if (handleMouseEnter && handleMouseLeave) {
          mediaWrap.removeEventListener('mouseenter', handleMouseEnter)
          mediaWrap.removeEventListener('mouseleave', handleMouseLeave)
        }
      }
    })
  }

  onMount(() => {
    applyCustomStyles()
    // setupHoverAnimation()
  })

  onPageIn(async () => {})

  onDestroy(() => {
    if (mediaWrap && handleMouseEnter && handleMouseLeave) {
      mediaWrap.removeEventListener('mouseenter', handleMouseEnter)
      mediaWrap.removeEventListener('mouseleave', handleMouseLeave)
    }
    tlIn?.kill()
    tlOut?.kill()
  })
}
